import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getRadicals,
  kanjiByRadicals,
  kanjiDetail,
  type KanjiDetail,
  type KanjiResult,
  type RadicalInfo,
} from "@/lib/search";

import {
  groupKanji,
  groupRadicals,
  radicalState,
  toggleRadical,
  type RadicalState,
} from "./radicalPalette";

/**
 * Radical lookup — README `4a`.
 *
 * **Not a search mode. An input method reached from a caret.** It opens *in the dock* rather than in
 * a modal precisely so the sentence and the hole you are filling stay on screen above it, and it
 * ends by inserting a character at that caret and re-parsing. The parent owns the caret, the
 * selection and the recents, because all three outlive any one opening of the picker: "keeps the
 * selected radicals for the session, since the next character you can't type often shares one".
 *
 * The palette lays out FLAT — no scrolling — which is why the cells are 26px.
 */

const CELL: Record<RadicalState, string> = {
  selected: "border-accent bg-accent font-semibold text-white",
  available: "border-line bg-panel text-ink hover:border-accent",
  // No border and no fill, only the disabled ink — an impossible radical should recede from the
  // grid rather than sit in it looking pressable.
  unavailable: "border-transparent bg-transparent text-ink-off cursor-default",
};

interface RadicalCellProps {
  info: RadicalInfo;
  state: RadicalState;
  onToggle: (radical: string) => void;
  /** 26px in the dock, where the palette must lay out flat; 28px on a phone, for the thumb. */
  size: number;
}

function RadicalCell({ info, state, onToggle, size }: RadicalCellProps) {
  const glyph = info.display ?? info.radical;
  return (
    <button
      type="button"
      disabled={state === "unavailable"}
      onClick={() => onToggle(info.radical)}
      style={{ width: size, height: size, fontSize: size >= 28 ? 16 : 15 }}
      // The 阝 pair renders as one glyph for two different radicals. Nudging it to the side it sits
      // on in a real kanji is the only thing that tells them apart.
      className={[
        "flex shrink-0 items-center justify-center rounded-[var(--r-2)] border font-jp leading-none",
        info.align === "left" ? "justify-start pl-[3px]" : "",
        info.align === "right" ? "justify-end pr-[3px]" : "",
        CELL[state],
      ].join(" ")}
      title={
        info.display && info.display !== info.radical
          ? `${glyph} (${info.radical})`
          : glyph
      }
    >
      {glyph}
    </button>
  );
}

export interface RadicalPickerProps {
  /** Which capture is being edited — the header reads `12:41 のカーソル位置に挿入`. */
  caretLabel: string;
  selected: string[];
  onSelectedChange: (next: string[]) => void;
  /** The ~9 radicals you actually reach for, most recent first. */
  recents: string[];
  onRemember: (radical: string) => void;
  /** Insert at the caret, re-parse, hand the dock back to results. */
  onInsert: (kanji: string) => void;
  /** Esc — back to results, edit state and selection both intact. */
  onClose: () => void;
  /** `6d`: the same picker stacked for a phone, with the palette scrolling inside the panel. */
  compact?: boolean;
}

export function RadicalPicker({
  caretLabel,
  selected,
  onSelectedChange,
  recents,
  onRemember,
  onInsert,
  onClose,
  compact = false,
}: RadicalPickerProps) {
  const [catalog, setCatalog] = useState<RadicalInfo[]>([]);
  const [kanji, setKanji] = useState<KanjiResult[]>([]);
  const [available, setAvailable] = useState<ReadonlySet<string> | null>(null);
  const [focused, setFocused] = useState(0);
  const [detail, setDetail] = useState<KanjiDetail | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getRadicals().then(({ radicals }) => {
      if (!cancelled) setCatalog(radicals);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Narrowing: the same request answers "which kanji" and "which radicals are still possible", so
  // the palette greys out in step with the results rather than after a second round-trip.
  useEffect(() => {
    if (selected.length === 0) {
      setKanji([]);
      setAvailable(null);
      setFocused(0);
      return;
    }
    let cancelled = false;
    void kanjiByRadicals(selected).then((result) => {
      if (cancelled) return;
      setKanji(result.kanji);
      setAvailable(new Set(result.availableRadicals));
      setFocused(0);
    });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  const focusedKanji = kanji[focused] ?? null;

  useEffect(() => {
    if (!focusedKanji) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetail(null);
    void kanjiDetail(focusedKanji.literal).then((found) => {
      if (!cancelled) setDetail(found);
    });
    return () => {
      cancelled = true;
    };
  }, [focusedKanji]);

  const onToggle = useCallback(
    (radical: string) => {
      if (!selected.includes(radical)) onRemember(radical);
      onSelectedChange(toggleRadical(selected, radical));
    },
    [selected, onSelectedChange, onRemember],
  );

  const insert = useCallback(() => {
    if (focusedKanji) onInsert(focusedKanji.literal);
  }, [focusedKanji, onInsert]);

  // Capture phase, on window: the caret stays in the sentence input above while the picker is open,
  // so ↵ and ⇥ would otherwise be swallowed by that field (↵ would commit the edit and close the
  // thing we are typing into). Capturing above the React root is what lets the picker win.
  const insertRef = useRef(insert);
  insertRef.current = insert;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        insertRef.current();
      } else if (event.key === "Tab") {
        event.preventDefault();
        event.stopPropagation();
        setFocused((i) => (kanji.length === 0 ? 0 : (i + (event.shiftKey ? -1 : 1) + kanji.length) % kanji.length));
      } else if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [kanji.length, onClose]);

  const byStroke = useMemo(() => groupRadicals(catalog), [catalog]);
  const byId = useMemo(() => new Map(catalog.map((r) => [r.radical, r])), [catalog]);
  const recentInfos = useMemo(
    () => recents.map((r) => byId.get(r)).filter((r): r is RadicalInfo => r !== undefined),
    [recents, byId],
  );
  const kanjiGroups = useMemo(() => groupKanji(kanji), [kanji]);

  const cellSize = compact ? 28 : 26;
  const palette = (
    <>
      {recentInfos.length > 0 && (
        <>
          <div className="flex items-start gap-[6px]">
            <span
              className="w-[18px] shrink-0 text-right text-[14px]/[1.4] font-semibold text-ink-3"
              title="最近使った部品"
            >
              ↺
            </span>
            <div className="flex flex-wrap gap-[2px]">
              {recentInfos.map((info) => (
                <RadicalCell
                  key={`recent-${info.radical}`}
                  info={info}
                  state={radicalState(info.radical, selected, available)}
                  onToggle={onToggle}
                  size={cellSize}
                />
              ))}
            </div>
          </div>
          <div className="h-px bg-line" />
        </>
      )}

      <div className="flex flex-col gap-[2px]">
        {byStroke.map((group) => (
          <div key={group.strokeCount} className="flex items-start gap-[2px]">
            <span className="w-[18px] shrink-0 pt-[6px] text-right font-mono text-[14px]/[1.4] font-medium text-ink-4">
              {group.strokeCount}
            </span>
            <div className="flex flex-wrap gap-[2px]">
              {group.items.map((info) => (
                <RadicalCell
                  key={info.radical}
                  info={info}
                  state={radicalState(info.radical, selected, available)}
                  onToggle={onToggle}
                  size={cellSize}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  if (compact) {
    return (
      <CompactPicker
        selected={selected}
        selectedInfo={byId}
        onSelectedChange={onSelectedChange}
        kanji={kanji}
        focusedKanji={focusedKanji}
        onFocus={(literal) => setFocused(kanji.findIndex((k) => k.literal === literal))}
        detail={detail}
        onInsert={insert}
        palette={palette}
      />
    );
  }

  return (
    <div className="@container flex h-full flex-col gap-[11px] px-[26px] pt-[13px] pb-[18px]">
      <div className="flex shrink-0 flex-wrap items-center gap-[10px]">
        <span className="inline-flex h-[22px] items-center rounded-[var(--r-2)] bg-accent px-2 font-jp text-[14px]/[1] font-semibold text-white">
          部
        </span>
        <span className="text-eyebrow font-semibold uppercase tracking-[.1em] text-ink-3">
          部品検索
        </span>
        <span className="text-secondary text-ink-4">{caretLabel} のカーソル位置に挿入</span>
        <span className="h-px flex-1 bg-line" />
        <span className="font-mono text-meta text-ink-4">
          ↵で挿入 · ⇥で次の候補 · Escで候補一覧へ
        </span>
      </div>

      <div className="flex min-h-0 flex-1 flex-wrap gap-5 overflow-auto @[980px]:flex-nowrap">
        {/* palette */}
        <div className="flex min-w-0 shrink basis-[596px] flex-col gap-[9px] @[980px]:w-[596px] @[980px]:grow-0">
          {palette}
        </div>

        {/* results */}
        <div className="flex min-w-0 flex-1 basis-[340px] flex-col gap-3 border-t border-line pt-4 @[980px]:border-t-0 @[980px]:border-l @[980px]:pt-0 @[980px]:pl-5">
          <div className="flex flex-wrap items-center gap-[7px]">
            <span className="text-eyebrow font-semibold uppercase tracking-[.1em] text-ink-3">
              選択中
            </span>
            {selected.map((radical) => {
              const info = byId.get(radical);
              return (
                <button
                  key={radical}
                  type="button"
                  onClick={() => onSelectedChange(toggleRadical(selected, radical))}
                  className="inline-flex items-center gap-[5px] rounded-[var(--r-2)] bg-accent px-[9px] py-1 font-jp text-[17px]/[1] text-white"
                >
                  {info?.display ?? radical}
                  <span className="font-ui text-[14px] opacity-75">×</span>
                </button>
              );
            })}
            {selected.length === 0 && (
              <span className="text-secondary text-ink-4">
                部品を選ぶと候補が絞り込まれます
              </span>
            )}
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onSelectedChange([])}
                className="rounded-[var(--r-2)] border border-line-strong bg-void px-[10px] py-1 text-meta font-semibold text-ink-2"
              >
                消去
              </button>
            )}
          </div>

          {selected.length > 0 && (
            <div className="flex items-baseline gap-2">
              <span className="text-title font-semibold text-ink">
                漢字{kanji.length}件
              </span>
              <span className="text-meta text-ink-4">画数順</span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-[8px]">
            {kanjiGroups.map((group) => (
              <span key={group.strokeCount} className="contents">
                <span className="w-[16px] shrink-0 text-right font-mono text-[14px]/[1.4] font-medium text-ink-4">
                  {group.strokeCount}
                </span>
                {group.items.map((result) => {
                  const isFocused = result.literal === focusedKanji?.literal;
                  return (
                    <button
                      key={result.literal}
                      type="button"
                      onClick={() => setFocused(kanji.indexOf(result))}
                      onDoubleClick={() => onInsert(result.literal)}
                      className={[
                        "h-[54px] w-[54px] shrink-0 rounded-[var(--r-3)] bg-panel font-jp text-[30px]/[1] text-ink",
                        isFocused
                          ? "border-2 border-accent shadow-[var(--sh-frame)]"
                          : "border border-line",
                      ].join(" ")}
                      title={result.meaning ?? undefined}
                    >
                      {result.literal}
                    </button>
                  );
                })}
              </span>
            ))}
          </div>

          {focusedKanji && (
            <FocusedKanjiCard
              result={focusedKanji}
              detail={detail}
              onInsert={() => onInsert(focusedKanji.literal)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * `6d` — the same picker, stacked for a phone.
 *
 * "Selection and the focused kanji pinned at the top, palette scrolling below. 28px cells keep the
 * whole thing thumb-reachable without the glyphs going soft." The desktop layout's two columns do
 * not fit 390px, and the *order* is the point: on a phone you want the answer and the Insert button
 * in reach of your thumb, with the palette you are done with pushed below.
 */
function CompactPicker({
  selected,
  selectedInfo,
  onSelectedChange,
  kanji,
  focusedKanji,
  onFocus,
  detail,
  onInsert,
  palette,
}: {
  selected: string[];
  selectedInfo: Map<string, RadicalInfo>;
  onSelectedChange: (next: string[]) => void;
  kanji: KanjiResult[];
  focusedKanji: KanjiResult | null;
  onFocus: (literal: string) => void;
  detail: KanjiDetail | null;
  onInsert: () => void;
  palette: React.ReactNode;
}) {
  const readings = detail ? [...detail.onReadings, ...detail.kunReadings] : [];

  return (
    <div className="flex h-full flex-col gap-[10px] bg-sunken px-[14px] py-3">
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <span className="inline-flex h-5 items-center rounded-[var(--r-2)] bg-accent px-[7px] font-jp text-[14px]/[1] font-semibold text-white">
          部
        </span>
        {selected.map((radical) => (
          <button
            key={radical}
            type="button"
            onClick={() => onSelectedChange(toggleRadical(selected, radical))}
            className="inline-flex items-center gap-1 rounded-[var(--r-4)] bg-accent-tint px-[9px] py-[3px] font-jp text-[16px]/[1] text-[color:var(--accent-deep)]"
          >
            {selectedInfo.get(radical)?.display ?? radical}
            <span className="font-ui text-[14px] opacity-70">×</span>
          </button>
        ))}
        <span className="flex-1" />
        <span className="text-meta text-ink-3">
          {selected.length === 0 ? "部品を選ぶ" : `漢字${kanji.length}件`}
        </span>
      </div>

      {focusedKanji && (
        <div className="flex shrink-0 items-center gap-[10px] rounded-[var(--r-3)] border border-accent-border bg-[color:var(--accent-tint-2)] px-[11px] py-[9px]">
          <span className="font-jp text-[27px]/[1] text-ink">{focusedKanji.literal}</span>
          <span className="min-w-0 flex-1 text-meta text-ink-2">
            {readings.length > 0 && <>{readings.slice(0, 3).join("・")} · </>}
            {detail?.meanings.slice(0, 2).join("; ") ?? focusedKanji.meaning}
            <br />
            {focusedKanji.strokeCount}画
            {detail?.inCollection != null && <> · {detail.inCollection}件のカードに出現</>}
          </span>
          {/* 44px minimum: nothing on a phone may be smaller than a thumb. */}
          <button
            type="button"
            onClick={onInsert}
            className="min-h-[44px] shrink-0 rounded-[var(--r-3)] bg-accent px-[14px] text-title font-semibold text-white"
          >
            挿入
          </button>
        </div>
      )}

      {kanji.length > 0 && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {kanji.map((result) => (
            <button
              key={result.literal}
              type="button"
              onClick={() => onFocus(result.literal)}
              className={[
                "h-[46px] w-[46px] shrink-0 rounded-[var(--r-3)] bg-panel font-jp text-[26px]/[1] text-ink",
                result.literal === focusedKanji?.literal
                  ? "border-2 border-accent"
                  : "border border-line",
              ].join(" ")}
            >
              {result.literal}
            </button>
          ))}
        </div>
      )}

      <div className="h-px shrink-0 bg-line" />

      {/* The one place the palette scrolls: 17 stroke groups do not fit 390px flat. */}
      <div className="flex min-h-0 flex-1 flex-col gap-[9px] overflow-auto">{palette}</div>
    </div>
  );
}

/**
 * The card that actually confirms you found the right character: readings, every meaning, stroke
 * count, grade, **how many of your own cards contain it**, and three example words.
 *
 * `detail` is null while it loads and stays null for a character KANJIDIC2 has no entry for, so
 * everything below the headword renders conditionally rather than as empty rows.
 */
function FocusedKanjiCard({
  result,
  detail,
  onInsert,
}: {
  result: KanjiResult;
  detail: KanjiDetail | null;
  onInsert: () => void;
}) {
  const readings = detail ? [...detail.onReadings, ...detail.kunReadings] : [];
  const meanings = detail?.meanings.length ? detail.meanings.join("; ") : result.meaning;

  return (
    <div className="flex shrink-0 flex-col gap-2 rounded-[var(--r-3)] border border-line-strong bg-panel px-[15px] py-[13px]">
      <div className="flex flex-wrap items-baseline gap-[10px]">
        <span className="font-jp text-[32px]/[1] text-ink">{result.literal}</span>
        {readings.length > 0 && (
          <span className="font-jp text-[15px]/[1.4] text-ink-2">
            {readings.slice(0, 6).join("・")}
          </span>
        )}
        <span className="rounded-[var(--r-2)] border border-line bg-void px-[7px] py-[3px] font-mono text-[14px]/[1.3] font-semibold text-ink-2">
          {result.strokeCount}画
        </span>
        {detail?.grade != null && (
          <span className="rounded-[var(--r-2)] border border-line bg-void px-[7px] py-[3px] text-[14px]/[1.3] font-semibold text-ink-2">
            学年{detail.grade}
          </span>
        )}
      </div>

      {meanings && <div className="text-body text-ink">{meanings}</div>}

      <div className="flex flex-wrap items-center gap-[9px] border-t border-line-soft pt-[9px]">
        <span className="text-secondary text-ink-3">
          {detail?.inCollection == null ? (
            // Null means the collection could not be read, which is not the same as zero.
            "件数不明"
          ) : (
            <>
              所持カード <strong className="text-ink">{detail.inCollection}</strong> 件に出現
            </>
          )}
        </span>
        {detail && detail.examples.length > 0 && (
          <>
            <span className="h-[14px] w-px bg-line" />
            <span className="font-jp text-[17px]/[1] text-ink-2">
              {detail.examples.join(" · ")}
            </span>
          </>
        )}
        <span className="flex-1" />
        <button
          type="button"
          onClick={onInsert}
          className="shrink-0 rounded-[var(--r-2)] bg-accent px-[13px] py-2 text-secondary font-semibold text-white"
        >
          カーソル位置に挿入
          <span className="ml-[7px] font-mono text-[14px] opacity-75">↵</span>
        </button>
      </div>
    </div>
  );
}
